import { useState, useEffect } from "react";
import { ScrollView, Pressable, ActivityIndicator, FlatList } from "react-native";
import styled from "styled-components/native";
import { useRouter } from "expo-router";
import { doc, getDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { SlotListSkeleton } from "@/components/Skeleton";

const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

function formatHour(h: number): string {
  if (h === 12) return "12:00 PM";
  return h > 12 ? `${h - 12}:00 PM` : `${h}:00 AM`;
}

function formatHourRange(h: number): string {
  const start = formatHour(h);
  const end = formatHour(h + 1);
  return `${start} — ${end}`;
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Styled ──

const Header = styled.View`padding: 20px 20px 8px;`;
const Subtitle = styled.Text`font-size: 13px; color: ${colors.inkMuted}; margin-top: 4px;`;

const DaySelector = styled.View`flex-direction: row; padding: 8px 16px 16px; gap: 6px;`;
const DayPill = styled.Pressable<{ active: boolean; isToday: boolean }>`
  flex: 1; align-items: center; padding: 10px 4px;
  border-radius: ${radius.md}px;
  background-color: ${(p) => p.active ? colors.brand : p.isToday ? colors.brand50 : colors.white};
  border: 1px solid ${(p) => p.active ? colors.brand : p.isToday ? colors.brandLight : colors.line};
`;
const DayPillDay = styled.Text<{ active: boolean }>`
  font-size: 10px; font-weight: ${font.bold};
  color: ${(p) => p.active ? "rgba(255,255,255,0.8)" : colors.inkMuted};
  text-transform: uppercase;
`;
const DayPillDate = styled.Text<{ active: boolean }>`
  font-size: 16px; font-weight: ${font.extrabold};
  color: ${(p) => p.active ? "#fff" : colors.ink};
  margin-top: 2px;
`;

const WeekNav = styled.View`flex-direction: row; align-items: center; justify-content: space-between; padding: 0 20px 8px;`;
const NavBtn = styled.Text`font-size: 14px; font-weight: ${font.bold}; color: ${colors.brand};`;
const WeekLabel = styled.Text`font-size: 13px; font-weight: ${font.bold}; color: ${colors.inkMuted};`;

const SlotCard = styled.View<{ status: "available" | "partial" | "full" | "past" }>`
  margin: 0 20px 8px;
  background-color: ${(p) => p.status === "past" ? colors.bg : colors.white};
  border-radius: ${radius.md}px;
  border: 1px solid ${(p) => p.status === "full" ? colors.amber200 : p.status === "partial" ? colors.brandLight : colors.line};
  padding: 14px 16px;
  flex-direction: row;
  align-items: center;
  opacity: ${(p) => p.status === "past" ? 0.4 : 1};
`;

const SlotTime = styled.View`flex: 1;`;
const SlotTimeText = styled.Text`font-size: 15px; font-weight: ${font.bold}; color: ${colors.ink};`;
const SlotRange = styled.Text`font-size: 12px; color: ${colors.inkMuted}; font-weight: ${font.medium}; margin-top: 2px;`;

const SlotBadge = styled.View<{ status: "available" | "partial" | "full" | "past" }>`
  padding: 6px 12px;
  border-radius: 999px;
  background-color: ${(p) => p.status === "full" ? colors.amber50 : p.status === "partial" ? colors.brand50 : colors.bg};
  border: 1px solid ${(p) => p.status === "full" ? colors.amber200 : p.status === "partial" ? colors.brandLight : colors.line};
`;
const SlotBadgeText = styled.Text<{ status: string }>`
  font-size: 12px; font-weight: ${font.bold};
  color: ${(p) => p.status === "full" ? colors.amber : p.status === "partial" ? colors.brandDark : colors.inkMuted};
`;

const LoadingContainer = styled.View`flex: 1; justify-content: center; align-items: center; padding: 40px;`;
const Spacer = styled.View`height: 32px;`;

export default function NetBookingsScreen() {
  const router = useRouter();
  const { club } = useClub();
  const [lanes, setLanes] = useState(4);
  const [bookingCounts, setBookingCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const now = new Date();
  const todayStr = toDateStr(now);

  // Calculate week days
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1 + weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  // Auto-select today when on current week
  useEffect(() => {
    if (weekOffset === 0) {
      const todayIdx = weekDays.findIndex((d) => toDateStr(d) === todayStr);
      if (todayIdx >= 0) setSelectedDayIndex(todayIdx);
    } else {
      setSelectedDayIndex(0);
    }
  }, [weekOffset]);

  const selectedDay = weekDays[selectedDayIndex];
  const selectedDateStr = toDateStr(selectedDay);

  // Load lane count
  useEffect(() => {
    if (!club?.id) return;
    async function loadLanes() {
      const netSnap = await getDoc(doc(db, "clubs", club!.id, "settings", "nets"));
      if (netSnap.exists()) setLanes(netSnap.data().lanes || 4);
    }
    loadLanes();
  }, [club?.id]);

  // Real-time listeners for booking counts on selected day
  useEffect(() => {
    if (!club?.id) return;
    setLoading(true);

    const unsubs: (() => void)[] = [];
    let loadedCount = 0;

    for (const hour of HOURS) {
      const timeStr = `${String(hour).padStart(2, "0")}:00`;
      const sessionId = `${selectedDateStr}_${timeStr}`;

      const unsub = onSnapshot(
        collection(db, "clubs", club.id, "netSessions", sessionId, "bookings"),
        (snap) => {
          setBookingCounts((prev) => ({ ...prev, [sessionId]: snap.size }));
          loadedCount++;
          if (loadedCount >= HOURS.length) setLoading(false);
        }
      );
      unsubs.push(unsub);
    }

    return () => unsubs.forEach((u) => u());
  }, [club?.id, selectedDateStr]);

  const getSlotStatus = (hour: number): "available" | "partial" | "full" | "past" => {
    const isPast = selectedDateStr < todayStr || (selectedDateStr === todayStr && hour <= now.getHours());
    if (isPast) return "past";
    const timeStr = `${String(hour).padStart(2, "0")}:00`;
    const sessionId = `${selectedDateStr}_${timeStr}`;
    const booked = bookingCounts[sessionId] || 0;
    if (booked >= lanes) return "full";
    if (booked > 0) return "partial";
    return "available";
  };

  const weekStart = weekDays[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const weekEnd = weekDays[6].toLocaleDateString("en-GB", { day: "numeric", month: "short" });

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Header>
          <Subtitle>{lanes} lanes · 1-hour slots</Subtitle>
        </Header>

        <WeekNav>
          <Pressable onPress={() => setWeekOffset((w) => w - 1)}><NavBtn>← Prev</NavBtn></Pressable>
          <WeekLabel>{weekStart} — {weekEnd}</WeekLabel>
          <Pressable onPress={() => setWeekOffset((w) => w + 1)}><NavBtn>Next →</NavBtn></Pressable>
        </WeekNav>

        {/* Day selector */}
        <DaySelector>
          {weekDays.map((d, i) => {
            const isToday = toDateStr(d) === todayStr;
            const active = i === selectedDayIndex;
            return (
              <DayPill key={i} active={active} isToday={isToday} onPress={() => setSelectedDayIndex(i)}>
                <DayPillDay active={active}>{d.toLocaleDateString("en-GB", { weekday: "short" })}</DayPillDay>
                <DayPillDate active={active}>{d.getDate()}</DayPillDate>
              </DayPill>
            );
          })}
        </DaySelector>

        {/* Slots for selected day */}
        {loading ? (
          <SlotListSkeleton count={8} />
        ) : (
          <>
            {HOURS.map((hour) => {
              const status = getSlotStatus(hour);
              const timeStr = `${String(hour).padStart(2, "0")}:00`;
              const sessionId = `${selectedDateStr}_${timeStr}`;
              const booked = bookingCounts[sessionId] || 0;

              return (
                <Pressable
                  key={hour}
                  onPress={() => status !== "past" && router.push(`/(app)/net-session?date=${selectedDateStr}&time=${timeStr}`)}
                  disabled={status === "past"}
                >
                  <SlotCard status={status}>
                    <SlotTime>
                      <SlotTimeText>{formatHour(hour)}</SlotTimeText>
                      <SlotRange>{formatHourRange(hour)}</SlotRange>
                    </SlotTime>
                    <SlotBadge status={status}>
                      <SlotBadgeText status={status}>
                        {status === "full" ? "Full" :
                         status === "past" ? "Past" :
                         booked > 0 ? `${booked}/${lanes} booked` :
                         "Available"}
                      </SlotBadgeText>
                    </SlotBadge>
                  </SlotCard>
                </Pressable>
              );
            })}
          </>
        )}

        <Spacer />
      </ContentWidth>
    </ScrollView>
  );
}
